// Parsing/formatting of box position specs — the browser-side mirror of
// server/src/utils/position_utils.py. Keep the two in sync: the UI uses this for
// live validation and the resolved-count hint, and the API re-validates with the
// Python version.
//
// A "position spec" lets an operator enter many slots at once:
//   "5"          -> [5]
//   "5-15"       -> [5,6,...,15]        (inclusive numeric range)
//   "5,6,7"      -> [5,6,7]
//   "5-8,12,20"  -> [5,6,7,8,12,20]
//   "A1"         -> ["A1"]              (non-numeric label, not expanded)

export const MAX_POSITION = 100;

export class PositionParseError extends Error {}

function posSortKey(p: string): [number, number, string] {
  return /^\d+$/.test(p) ? [0, parseInt(p, 10), ''] : [1, 0, p];
}

/**
 * Expand a spec into a sorted, de-duplicated list of position tokens.
 * Throws PositionParseError on any malformed or out-of-range input.
 */
export function parsePositions(spec: string, maxPosition = MAX_POSITION): string[] {
  const text = (spec ?? '').toString().trim();
  if (!text) throw new PositionParseError('Position cannot be empty');

  const seen = new Set<string>();
  const ordered: string[] = [];

  const add = (token: string) => {
    if (seen.has(token)) {
      throw new PositionParseError(`Position '${token}' is listed more than once`);
    }
    seen.add(token);
    ordered.push(token);
  };

  for (const raw of text.split(',')) {
    const tok = raw.trim();
    if (!tok) continue;

    // Numeric range "A-B" (only when both ends are plain integers).
    const dashIdx = tok.indexOf('-', 1);
    if (dashIdx > 0) {
      const left = tok.slice(0, dashIdx).trim();
      const right = tok.slice(dashIdx + 1).trim();
      if (/^\d+$/.test(left) && /^\d+$/.test(right)) {
        const start = parseInt(left, 10);
        const end = parseInt(right, 10);
        if (start < 1 || end < 1) throw new PositionParseError('Positions must be 1 or greater');
        if (start > end) throw new PositionParseError(`Range '${tok}' is backwards — start must be ≤ end`);
        if (end > maxPosition) throw new PositionParseError(`Position ${end} exceeds the box capacity of ${maxPosition}`);
        for (let n = start; n <= end; n++) add(String(n));
        continue;
      }
    }

    if (/^\d+$/.test(tok)) {
      const n = parseInt(tok, 10);
      if (n < 1) throw new PositionParseError('Positions must be 1 or greater');
      if (n > maxPosition) throw new PositionParseError(`Position ${n} exceeds the box capacity of ${maxPosition}`);
      add(String(n));
    } else {
      add(tok);
    }
  }

  if (ordered.length === 0) throw new PositionParseError('No valid positions were provided');

  ordered.sort((a, b) => {
    const ka = posSortKey(a);
    const kb = posSortKey(b);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2].localeCompare(kb[2]);
  });
  return ordered;
}

/** Render a list of tokens back into a compact spec, e.g. [5,6,7,8,12] -> "5-8,12". */
export function formatPositions(positions: string[]): string {
  const nums = Array.from(new Set(positions.filter((p) => /^\d+$/.test(String(p))).map((p) => parseInt(p, 10)))).sort((a, b) => a - b);
  const labels = Array.from(new Set(positions.filter((p) => !/^\d+$/.test(String(p))).map(String))).sort();

  const parts: string[] = [];
  let i = 0;
  while (i < nums.length) {
    const start = nums[i];
    let end = start;
    while (i + 1 < nums.length && nums[i + 1] === end + 1) {
      end += 1;
      i += 1;
    }
    parts.push(start === end ? String(start) : `${start}-${end}`);
    i += 1;
  }
  parts.push(...labels);
  return parts.join(',');
}

/**
 * Convenience for the UI: returns the resolved count and any error message
 * without throwing. `count` is 0 when the spec is empty or invalid.
 */
export function resolvePositions(spec: string): { positions: string[]; count: number; error: string } {
  if (!spec || !spec.trim()) return { positions: [], count: 0, error: '' };
  try {
    const positions = parsePositions(spec);
    return { positions, count: positions.length, error: '' };
  } catch (e) {
    return { positions: [], count: 0, error: e instanceof Error ? e.message : 'Invalid position' };
  }
}
